import {Component, OnDestroy, OnInit, ViewChild} from '@angular/core';
import {ServerPageRequest} from '../../classes/common/table/server-page-request';
import {DisplayedColumnInf} from '../../interfaces/common/table/displayed-column-inf';
import {DateTimePipe} from '../../pipes/date-time/date-time.pipe';
import {UserPrivilegeService} from '../../services/common/user-privilege/user-privilege.service';
import {FileRepositoryApiService} from '../../services/api/users/file-repository/file-repository-api.service';
import {AttributeManipulationGroupsApiService} from '../../services/api/settings/call-flow-configurations/attribute-manipulation-groups/attribute-manipulation-groups-api.service';
import {PageEvent} from '@angular/material/paginator';
import {TableColumnsConfigurationDialogComponent} from '../../dialogs/table-columns-configuration-dialog/table-columns-configuration-dialog.component';
import {MatDialog} from '@angular/material/dialog';
import {FileRepositoryStatusPipe} from './file-repository-status.pipe';
import {FileRepositoryDialogComponent} from './file-repository-dialog/file-repository-dialog.component';
import {SnackBarService} from '../../services/common/snack-bar/snack-bar.service';
import {LogicalConjunctionService} from '../../services/common/logical-conjunction/logical-conjunction.service';
import * as _ from 'lodash';
import {ConfirmationDialogComponent} from '../../dialogs/confirmation-dialog/confirmation-dialog.component';
import {ArmTableComponent} from '../../components/arm-table/arm-table.component';
import {LoaderService} from '../../services/common/loader-service/loader.service';
import {interval} from 'rxjs';
import {RoleTypeEnum} from '../../services/enums/role-type.enum';

@Component({
  selector: 'file-repository',
  templateUrl: './file-repository.component.html',
  styleUrls: ['./file-repository.component.scss']
})
export class FileRepositoryComponent implements OnInit, OnDestroy {

  data: any = {users: [], totalElements: 0};

  serverPageRequest: ServerPageRequest = new ServerPageRequest({
    pageIndex: 0,
    pageSize: 25,
    length: 0
  });

  @ViewChild(ArmTableComponent) armTable;

  displayedColumns: Array<DisplayedColumnInf> = [
    {name: 'status', display: 'Status', selected: true, width: 200, transformer: FileRepositoryStatusPipe},
    {name: 'name', display: 'Name', selected: true, width: 200},
    {name: 'numberOfUsers', display: 'Number of users', selected: true, width: 200},
    {name: 'description', display: 'Description', selected: true, width: 200},
    {name: 'lastTimestamp', display: 'Last update', selected: true, width: 200, transformer: DateTimePipe},
  ];

  isUnauthorized: boolean;
  selection: any;
  normalizationGroups: any[];
  searchData: { data: {} };
  search: any;
  searchTerms: any;

  private readonly TABLE_REFRESH_TIME = 30000;
  private autoRefreshObservable;

  constructor(private fileRepositoryApiService: FileRepositoryApiService,
              private userPrivilege: UserPrivilegeService,
              private attributeManipulationGroupsApiService: AttributeManipulationGroupsApiService,
              public dialog: MatDialog,
              private snackBarService: SnackBarService,
              private logicalConjunctionService: LogicalConjunctionService, private loaderService: LoaderService) { }

  ngOnInit(): void {
    this.isUnauthorized = !this.userPrivilege.isAuthorized(RoleTypeEnum.USER_MANAGEMENT);
    this.initData();
    this.setSearchOptions();
    this.startRefreshObservable();
  }

  ngOnDestroy(): void {
    this.unsubscribeRefreshObservable();
  }

  private initData(): void {
    const params = {...this.serverPageRequest};
    this.loaderService.showMainContainer();
    Promise.all([this.fileRepositoryApiService.getFileRepositories(params),
      this.attributeManipulationGroupsApiService.getAttributeManipulationGroups().toPromise()]
    ).then(data => {
      this.data = data[0];
      this.normalizationGroups = data[1];
      this.loaderService.hideMainContainer();
    }).catch((error) => {
      this.loaderService.hideMainContainer();
    });
  }

  refresh(): void {
    const params = {...this.search, ...this.serverPageRequest};
    this.loaderService.showMainContainer();
    this.fileRepositoryApiService.getFileRepositories(params).then((data) => {
      this.data = data;
      this.loaderService.hideMainContainer();
    }).catch((error) => {
      console.log(`Exception was thrown in refresh registered users, reason: ${error}`);
      this.loaderService.hideMainContainer();
    });
  }

  private setSearchOptions(): void {
    this.searchData = {
      data: {},
    };
  }

  addItem(): void {
    this.fileRepositoryApiService.prepareDataForFileRepositoryAdd().then(data => {
      const dialogRef = this.dialog.open(FileRepositoryDialogComponent, {
        width: '60%',
        data: {
          isEditMode: false,
          selectedItem: data[0],
          attributes: data[1],
          normalizationGroupsOptions: data[2],
        }
      });

      dialogRef.componentInstance.onApply.subscribe(result => {
        const fileRepositoryJson = this.prepareFileRepositoryJson(result);
        this.fileRepositoryApiService.addFileRepository(result.file, fileRepositoryJson)
          .then(() => {
            dialogRef.close();
            this.snackBarService.display(`"${result.name}" successfully added`);
            this.refresh();
          }).catch((error) =>  console.log(`Exception was thrown in add file repository, reason: ${error}`));
      });
    });
  }

  private prepareFileRepositoryJson(fileRepository): any {
    let fileRepoToSend = _.cloneDeep(fileRepository);
    fileRepoToSend = this.constructFileRepository(fileRepoToSend);
    delete fileRepoToSend.file;
    delete fileRepoToSend.unique;
    return fileRepoToSend;
  }

  private constructFileRepository(fileRepository): any {
    fileRepository.unique === -1 ? fileRepository.isNameUnique = true
      : fileRepository.uniquePropertyId = fileRepository.unique;
    const nameProp = fileRepository.dicAttrMapping.find(prop => prop.dicAttrId === -1);
    if (nameProp && (nameProp.fileRepositoryAttrName != null ||  nameProp.fileRepositoryAttrLocation != null)) {
      if (fileRepository.isFileHasHeaders) {
        fileRepository.nameFileMapping = nameProp.fileRepositoryAttrName;
      } else {
        fileRepository.nameFileLocation = nameProp.fileRepositoryAttrLocation;
      }
      const index = fileRepository.dicAttrMapping.findIndex(prop => prop.dicAttrId === -1);
      fileRepository.dicAttrMapping.splice(index, 1);

      fileRepository.dicAttrMapping.forEach(attrMapping => {
        if (fileRepository.isFileHasHeaders) {
          delete attrMapping.fileRepositoryAttrLocation;
        } else {
          delete attrMapping.fileRepositoryAttrName;
        }
      });
    } else {
      fileRepository.nameFileMapping = null;
      fileRepository.nameFileLocation = null;
    }

    return this.removeEmptyMappings(fileRepository);
  }

  private removeEmptyMappings(repository): any {
    repository.dicAttrMapping = repository.dicAttrMapping.filter((mapping) => {
      if (repository.isFileHasHeaders) {
        return mapping.fileRepositoryAttrName !== null && mapping.fileRepositoryAttrName !== undefined;
      } else {
        return mapping.fileRepositoryAttrLocation !== null && mapping.fileRepositoryAttrLocation !== undefined;
      }
    });

    return repository;
  }

  editItem(): void {
    this.fileRepositoryApiService.prepareDataForFileRepositoryEdit(this.selection.id).then(data => {
      const dialogRef = this.dialog.open(FileRepositoryDialogComponent, {
        width: '60%',
        data: {
          isEditMode: true,
          selectedItem: data[0],
          attributes: data[1],
          normalizationGroupsOptions: data[2],
        }
      });

      dialogRef.componentInstance.onApply.subscribe(result => {
        const fileRepositoryJson = this.prepareFileRepositoryJson(result);
        this.fileRepositoryApiService.editFileRepository(result.file, fileRepositoryJson)
          .then(() => {
            dialogRef.close();
            this.snackBarService.display(`"${result.name}" successfully updated`);
            this.refresh();
          })
          .catch((error) =>  console.log(`Exception was thrown in edit file repository, reason: ${error}`));
      });
    });
  }

  removeItem(): void {
    const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
      width: '460px',
      data: {
        message: 'Deleting the file repository will cause deletion of all users related to this repository.' +
          `
          Are you sure you want to delete the file repository: "${this.selection.name}" ?`
      }
    });

    dialogRef.componentInstance.onApply.subscribe(result => {
      this.fileRepositoryApiService.deleteFileRepository(this.selection.id).then(() => {
        dialogRef.close();
        this.refresh();
        this.snackBarService.display(`${this.selection.name} successfully deleted`);
      });
    });
  }

  openColumnsConfiguration(): void {
    const dialogRef = this.dialog.open(TableColumnsConfigurationDialogComponent, {
      width: '350px',
      data: {
        columns: this.displayedColumns
      }
    });

    dialogRef.componentInstance.onApply.subscribe(() => {
      dialogRef.close();
    });
  }

  select(selection: any): void {
    this.selection = selection;
  }

  pageChanged(pageInfo: PageEvent): void {
    this.serverPageRequest = new ServerPageRequest(pageInfo);
    this.refresh();
  }

  onSearch(search: any): void {
    this.armTable.firstPage();
    this.serverPageRequest.firstPage();
    this.search = search;
    this.refresh();
  }

  private startRefreshObservable(): void {
    this.unsubscribeRefreshObservable();

    this.autoRefreshObservable = interval(this.TABLE_REFRESH_TIME).subscribe(() => this.refresh());
  }

  private unsubscribeRefreshObservable(): void {
    if (this.autoRefreshObservable) {
      this.autoRefreshObservable.unsubscribe();
    }
  }
}
